// ═══════════════════════════════════════════════════════════════════
// API CLIENT
//
// Every payload crossing this boundary is already encrypted. Nothing in
// this file can see a balance, and the passphrase is never referenced.
//
// Offline behaviour: responses are mirrored into localStorage as
// CIPHERTEXT. When the network is unavailable we serve that cache, which
// still has to be decrypted with the passphrase — so an offline device is
// no more readable than an online one.
// ═══════════════════════════════════════════════════════════════════

const CACHE_PREFIX = 'jj_vault_cache_v1:';

const cacheKey = (vaultId, what) => `${CACHE_PREFIX}${vaultId}:${what}`;

function readCache(vaultId, what) {
  try {
    const raw = localStorage.getItem(cacheKey(vaultId, what));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function writeCache(vaultId, what, value) {
  try { localStorage.setItem(cacheKey(vaultId, what), JSON.stringify(value)); } catch { /* quota */ }
}

export function clearCache(vaultId) {
  try {
    localStorage.removeItem(cacheKey(vaultId, 'settings'));
    localStorage.removeItem(cacheKey(vaultId, 'snapshots'));
  } catch { /* ignore */ }
}

/** Thrown when we genuinely could not reach the server AND had no cache. */
export class OfflineError extends Error {
  constructor() {
    super('You appear to be offline and there is no cached copy on this device yet.');
    this.name = 'OfflineError';
  }
}

async function req(path, options = {}) {
  const res = await fetch(path, {
    ...options,
    headers: { 'content-type': 'application/json', ...(options.headers || {}) },
  });
  if (res.status === 404) return { notFound: true };
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try { const b = await res.json(); if (b?.error) msg = b.error; } catch { /* ignore */ }
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// ── Settings ────────────────────────────────────────────────────────

/**
 * @returns {{ payload: {iv,ct}|null, updatedAt: string|null, fromCache: boolean, isNew: boolean }}
 */
export async function getSettings(vaultId) {
  try {
    const out = await req(`/api/vault/${vaultId}/settings`);
    if (out.notFound) return { payload: null, updatedAt: null, fromCache: false, isNew: true };
    const value = { iv: out.iv, ct: out.ct, updatedAt: out.updatedAt };
    writeCache(vaultId, 'settings', value);
    return { payload: { iv: out.iv, ct: out.ct }, updatedAt: out.updatedAt, fromCache: false, isNew: false };
  } catch (e) {
    if (e.status) throw e; // a real server error, not a network failure
    const cached = readCache(vaultId, 'settings');
    if (!cached) throw new OfflineError();
    return { payload: { iv: cached.iv, ct: cached.ct }, updatedAt: cached.updatedAt, fromCache: true, isNew: false };
  }
}

export async function putSettings(vaultId, payload) {
  const out = await req(`/api/vault/${vaultId}/settings`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  writeCache(vaultId, 'settings', { ...payload, updatedAt: out.updatedAt });
  return out;
}

// ── Snapshots ───────────────────────────────────────────────────────

/**
 * @returns {{ snapshots: Array, fromCache: boolean }}
 */
export async function listSnapshots(vaultId) {
  try {
    const out = await req(`/api/vault/${vaultId}/snapshots`);
    const snapshots = out.notFound ? [] : (out.snapshots || []);
    writeCache(vaultId, 'snapshots', snapshots);
    return { snapshots, fromCache: false };
  } catch (e) {
    if (e.status) throw e;
    const cached = readCache(vaultId, 'snapshots');
    if (!cached) throw new OfflineError();
    return { snapshots: cached, fromCache: true };
  }
}

export async function createSnapshot(vaultId, snapshot) {
  const out = await req(`/api/vault/${vaultId}/snapshots`, {
    method: 'POST',
    body: JSON.stringify(snapshot),
  });
  // Keep the offline cache in step without a second round trip.
  const cached = readCache(vaultId, 'snapshots') || [];
  writeCache(vaultId, 'snapshots', [snapshot, ...cached]);
  return out;
}

export async function deleteSnapshot(vaultId, id) {
  const out = await req(`/api/vault/${vaultId}/snapshots/${id}`, { method: 'DELETE' });
  const cached = readCache(vaultId, 'snapshots') || [];
  writeCache(vaultId, 'snapshots', cached.filter(s => s.id !== id));
  return out;
}

export const isOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine);
